import React, { useCallback, useMemo } from "react";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Theme,
} from "@mui/material";
import { NavLink as RouterNavLink } from "react-router-dom";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { shallowEqual, useSelector } from "react-redux";
import { OrganizationAvatar } from "@basecomponents/OrganizationAvatar";
import { MENU_ITEM_CONTEXT_SWITCH_TYPOGRAPHY } from "./types/constants";
import { useGetOrgUnitNavHeader } from "@features/ecommerce/hooks/useGetOrgUnitNavHeader";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import { getManagedSecurityGroups } from "../../../../functions/securityGroupHelper";
import { IRootState } from "../../../../modules";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { DynamicIcon } from "shared-ui/src/components/DynamicIcon";
import useProfileDrawersEnabled from "@features/unified-user-header-feature/hooks/useProfileDrawersEnabled";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useStorefrontSwitcher } from "@features/unified-side-nav-feature/storefront-switcher/StorefrontSwitcherContext";
import { standardsManagementAdminRoles } from "@features/ecommerce/hooks/standardsAuthorization";
import { hasTenantId } from "../../components/TenantRouteGuard";

const iconColor = { color: "action.active" };

interface OrganizationMenuProps {
  orgUnitId?: number;
  orgLogoUrl?: string;
  accountName?: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onOpenCompanyProfileDrawer?: () => void;
}

export default function CourtesyOrgUnitMenu({
  orgUnitId,
  orgLogoUrl,
  accountName,
  anchorEl,
  open,
  onClose,
  onOpenCompanyProfileDrawer,
}: OrganizationMenuProps) {
  const claims = useClaims();
  const { getClaimValue, hasPermission, hasRoleIn } = claims;
  const getPermissionResourceIdentifiers =
    claims.getPermissionResourceIdentifiers ?? (() => []);
  const { enabled: standardsShopEnabled } = useFeatureFlag(
    "nomads.enable-standards-shop",
    false,
  );
  const {
    enabled: orderApprovalsEnabled,
    loading: orderApprovalsLoading,
  } = useFeatureFlag("nomads.order-approvals", false);
  const profileDrawersEnabled = useProfileDrawersEnabled();
  const myTeams = useSelector(
    (state: IRootState) => state.teamRtk.myTeams,
    shallowEqual,
  );
  const userId = getClaimValue("userId");
  const myManagedSecurityGroups = getManagedSecurityGroups(
    userId,
    myTeams as Parameters<typeof getManagedSecurityGroups>[1],
  );

  const { data: orgUnitInfo } = useGetOrgUnitNavHeader(
    orgUnitId ?? 0,
    !!orgUnitId,
  );
  const { orgUnitName, displayName: displayName1 } = orgUnitInfo ?? {};
  const showOrgUnitName = Boolean(orgUnitName && orgUnitName.length > 0);
  const displayName = accountName || displayName1 || orgUnitName || "Org Unit";

  const shiComBaseUrl = useMemo(() => getShiComBaseUrl(), []);

  const handleLinkKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
      }
    },
    [],
  );

  const orderApprovals =
    hasPermission(ShiOnePermission.webOrderApprove) ||
    hasPermission(ShiOnePermission.webOrderViewPendingOu);
  const viewStandards = hasPermission(ShiOnePermission.featuredProductsView);
  const storefrontSwitcher = useStorefrontSwitcher();
  const changeOrgUnit = storefrontSwitcher.enabled
    ? storefrontSwitcher.canOpenStorefront || storefrontSwitcher.canOpenTenant
    : hasPermission(ShiOnePermission.orgUnitChange);
  const switchLabel = storefrontSwitcher.enabled ? "SWITCH STOREFRONT" : "SWITCH CATALOG";
  const settingsGeneral = hasPermission(ShiOnePermission.settingsGeneralManage);
  const settingsIntegrations = hasPermission(
    ShiOnePermission.settingsIntegrationManage,
  );
  const hasTenant = hasTenantId(getClaimValue("tenantId"));
  const settingsUserManagement = hasPermission(
    ShiOnePermission.settingsUserManage,
  ) && hasTenant;
  const isAdmin = hasRoleIn("CustomerAdmin", "ShiAdmin");
  const manageAddresses =
    isAdmin && hasPermission(ShiOnePermission.streetAddressVisibilityOu);
  const settingsSecurityGroups =
    hasTenant && (isAdmin || myManagedSecurityGroups.length > 0);

  const isStorefrontsAdmin = hasRoleIn(...standardsManagementAdminRoles);
  const canManageStorefronts =
    isStorefrontsAdmin ||
    hasPermission(ShiOnePermission.storefrontManage) ||
    getPermissionResourceIdentifiers(ShiOnePermission.storefrontManage).length >
      0;

  const showOrderApprovals =
    orderApprovals && orderApprovalsEnabled && !orderApprovalsLoading;
  const hasOrgUnitSection =
    showOrderApprovals || viewStandards || manageAddresses;
  const hasSettingsSection =
    settingsGeneral ||
    settingsIntegrations ||
    settingsUserManagement ||
    settingsSecurityGroups ||
    canManageStorefronts;

  const sections = {
    orgHeader: showOrgUnitName,
    orgActions: hasOrgUnitSection,
    settings: hasSettingsSection,
    switchCatalog: changeOrgUnit,
  };

  const hasContentBeforeSwitchCatalog =
    sections.orgHeader || sections.orgActions || sections.settings;
  const hasContentBeforeSignOut =
    hasContentBeforeSwitchCatalog || sections.switchCatalog;
  const hasStorefrontsEnabled = canManageStorefronts;

  return (
    <Menu
      id="unity-courtesy-header-org-unit-menu"
      aria-label={`Courtesy Org Unit Menu: ${displayName}`}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      elevation={8}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            mt: -1,
            backgroundColor: "background.default",
            minWidth: { xs: "280px", sm: 300 },
            maxWidth: { xs: "calc(100vw - 32px)", sm: 400 },
            width: { xs: "calc(100vw - 32px)", sm: "300px" },
          },
        },
        list: {
          "aria-labelledby": "unity-courtesy-header-org-unit-menu",
          sx: {
            "& .MuiListItemText-root": {
              marginLeft: { xs: "12px", sm: "16px" },
            },
            "& .MuiListItemText-primary": {
              fontSize: { xs: "13px", sm: "14px" },
              fontWeight: "400",
              color: "text.primary",
            },
            "& .MuiMenuItem-root": {
              minHeight: { xs: "44px", sm: "48px" },
              paddingY: { xs: 1, sm: 1.5 },
            },
            "& .MuiListItemIcon-root": {
              minWidth: { xs: "40px", sm: "44px" },
            },
          },
        },
      }}
    >
      <MenuItem
        onClick={profileDrawersEnabled ? () => {
          onClose();
          onOpenCompanyProfileDrawer?.();
        } : undefined}
        {...(profileDrawersEnabled
          ? telemetryProps({ name: "org_unit_company_profile", section: "org_unit_menu", label: "Company Profile Header" })
          : {})}
        sx={{
          cursor: profileDrawersEnabled ? "pointer" : "default",
          ...(profileDrawersEnabled ? {} : {
            "&:hover": {
              backgroundColor: "transparent",
            },
            "&:focus": {
              backgroundColor: "transparent",
            },
          }),
        }}
        tabIndex={profileDrawersEnabled ? 0 : -1}
        aria-label={profileDrawersEnabled ? `Open company profile: ${displayName}` : `Organization Account: ${displayName}`}
      >
        <ListItemIcon>
          <OrganizationAvatar
            logoUrl={orgLogoUrl}
          />
        </ListItemIcon>
        <ListItemText
          primary={displayName}
          slotProps={{
            primary: {
              noWrap: true,
              variant: "body1",
              title: displayName,
            },
          }}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: { xs: "15px", sm: "16px" },
            },
          }}
        />
      </MenuItem>
      <Divider />
      {showOrgUnitName && (
        <ListSubheader
          disableSticky
          aria-label={`Org Unit: ${orgUnitName}`}
          sx={{
            backgroundColor: "background.default",
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: 1.2,
            paddingTop: { xs: 1.5, sm: 2 },
            paddingBottom: { xs: 1.5, sm: 2 },
            paddingX: { xs: 2, sm: 2 },
            fontSize: { xs: "13px", sm: "14px" },
          }}
        >
          {orgUnitName}
        </ListSubheader>
      )}
      {showOrderApprovals && (
        <MenuItem
          component={RouterNavLink}
          to={AppendIfPrefixExists("/orders/management?tab=pendingApprovals")}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_order_approvals", section: "org_unit_menu", label: "Order Approvals" })}
          aria-label="Order Approvals"
        >
          <ListItemIcon>
            <DynamicIcon iconName="HowToReg" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="Order Approvals" />
        </MenuItem>
      )}
      {viewStandards && (
        <MenuItem
          component={standardsShopEnabled ? RouterNavLink : "a"}
          to={standardsShopEnabled ? AppendIfPrefixExists("/standards-shop") : undefined}
          href={standardsShopEnabled ? undefined : `${shiComBaseUrl}/shop/standards`}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_standards", section: "org_unit_menu", label: "Standards" })}
          aria-label="Standards"
        >
          <ListItemIcon>
            <DynamicIcon iconName="ImportantDevices" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="Standards" />
        </MenuItem>
      )}
      {manageAddresses && (
        <MenuItem
          component="a"
          href={`${shiComBaseUrl}/account/address-org-manager`}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_manage_addresses", section: "org_unit_menu", label: "Manage Addresses" })}
          aria-label="Manage Addresses"
        >
          <ListItemIcon>
            <DynamicIcon iconName="LocationCity" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="Manage Addresses" />
        </MenuItem>
      )}
      {(showOrgUnitName || hasOrgUnitSection) && hasSettingsSection && (
        <Divider />
      )}
      {hasSettingsSection && (
        <ListSubheader
          disableSticky
          aria-label="SHI One Settings"
          sx={{
            backgroundColor: "background.default",
            color: "text.secondary",
            fontSize: { xs: "12px", sm: "14px" },
          }}
        >
          SHI One Settings
        </ListSubheader>
      )}
      {settingsGeneral && (
        <MenuItem
          component={RouterNavLink}
          to={AppendIfPrefixExists("/settings/general")}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_settings_general", section: "org_unit_menu", label: "General" })}
          aria-label="General"
          sx={(theme: Theme) => ({
            '&[aria-current="page"]': {
              backgroundColor: theme.palette.action.selected,
            },
          })}
        >
          <ListItemIcon>
            <DynamicIcon iconName="MiscellaneousServices" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="General" />
        </MenuItem>
      )}
      {settingsIntegrations && (
        <MenuItem
          component={RouterNavLink}
          to={AppendIfPrefixExists("/settings/integrations")}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_settings_integrations", section: "org_unit_menu", label: "Integrations" })}
          aria-label="Integrations"
          sx={(theme: Theme) => ({
            '&[aria-current="page"]': {
              backgroundColor: theme.palette.action.selected,
            },
          })}
        >
          <ListItemIcon>
            <DynamicIcon iconName="HubOutlined" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="Integrations" />
        </MenuItem>
      )}
      {settingsUserManagement && (
        <MenuItem
          component={RouterNavLink}
          to={AppendIfPrefixExists("/settings/user-management")}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_settings_users", section: "org_unit_menu", label: "User Management" })}
          aria-label="User Management"
          sx={(theme: Theme) => ({
            '&[aria-current="page"]': {
              backgroundColor: theme.palette.action.selected,
            },
          })}
        >
          <ListItemIcon>
            <DynamicIcon iconName="Person" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="User Management" />
        </MenuItem>
      )}
      {settingsSecurityGroups && (
        <MenuItem
          component={RouterNavLink}
          to={AppendIfPrefixExists("/settings/team-management")}
          onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_settings_sec_groups", section: "org_unit_menu", label: "Security Groups" })}
          aria-label="Security Groups"
          sx={(theme: Theme) => ({
            '&[aria-current="page"]': {
              backgroundColor: theme.palette.action.selected,
            },
          })}
        >
          <ListItemIcon>
            <DynamicIcon iconName="Groups" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="Security Groups" />
        </MenuItem>
      )}
      {hasStorefrontsEnabled && (
        <MenuItem
        component={RouterNavLink}
        to={AppendIfPrefixExists("/settings/storefronts")}
        onClick={onClose}
          onKeyDown={handleLinkKeyDown}
          {...telemetryProps({ name: "org_unit_settings_storefronts", section: "org_unit_menu", label: "Storefronts" })}
          aria-label="Storefronts"
          sx={(theme: Theme) => ({
          '&[aria-current="page"]': {
            backgroundColor: theme.palette.action.selected,
        }
        })}>
          <ListItemIcon>
            <DynamicIcon iconName="Storefront" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText primary="Storefronts" />
          </MenuItem>
      )}
      {changeOrgUnit && hasContentBeforeSwitchCatalog && <Divider />}
      {changeOrgUnit && (
        <MenuItem
          component={storefrontSwitcher.enabled ? "li" : "a"}
          href={storefrontSwitcher.enabled ? undefined : `${shiComBaseUrl}/account/ChangeCatalog`}
          onClick={() => {
            onClose();
            if (storefrontSwitcher.enabled) {
              storefrontSwitcher.open("org_unit_menu");
            }
          }}
          onKeyDown={
            storefrontSwitcher.enabled ? undefined : handleLinkKeyDown
          }
          {...telemetryProps({ name: "org_unit_switch_catalog", section: "org_unit_menu", label: switchLabel })}
          aria-label={switchLabel}
        >
          <ListItemIcon>
            <DynamicIcon iconName="SwapHoriz" color={iconColor} size="smaller" />
          </ListItemIcon>
          <ListItemText
            primary={switchLabel}
            sx={{
              "& .MuiListItemText-primary": {
                ...MENU_ITEM_CONTEXT_SWITCH_TYPOGRAPHY,
              },
            }}
          />
        </MenuItem>
      )}
      {hasContentBeforeSignOut && <Divider />}
      <MenuItem
        component={RouterNavLink}
        to={AppendIfPrefixExists("/logout")}
        onClick={onClose}
        onKeyDown={handleLinkKeyDown}
        {...telemetryProps({ name: "org_unit_sign_out", section: "org_unit_menu", label: "Sign Out" })}
        aria-label="SIGN OUT"
        sx={(theme: Theme) => ({
          '&[aria-current="page"]': {
            backgroundColor: theme.palette.action.selected,
          },
        })}
      >
        <ListItemIcon>
            <DynamicIcon iconName="Logout" color={iconColor} size="smaller" />
        </ListItemIcon>
        <ListItemText
          primary="SIGN OUT"
          sx={{
            "& .MuiListItemText-primary": {
              ...MENU_ITEM_CONTEXT_SWITCH_TYPOGRAPHY,
            },
          }}
        />
      </MenuItem>
    </Menu>
  );
}
